import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { messageAPI } from '../services/api';
import './ChatBox.css';

const ChatBox = ({ otherUserId, otherUserName, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);


  // Track if this is the initial load to auto-scroll
  const isInitialLoadRef = useRef(true);
  
  useEffect(() => {
    if (otherUserId) {
      isInitialLoadRef.current = true;
      loadMessages(true);
    }
  }, [otherUserId]);

  useEffect(() => {
    // Only auto-scroll to bottom on initial load completion
    // Don't scroll when loading older messages (preserve scroll position)
    if (isInitialLoadRef.current && !loading && messages.length > 0) {
      isInitialLoadRef.current = false;
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [loading, messages.length]);

  // Scroll to bottom when a new message is added (user sends a message)
  const prevMessagesLengthRef = useRef(0);
  useEffect(() => {
    // If messages increased and we're not loading older messages, scroll to bottom
    if (messages.length > prevMessagesLengthRef.current && !loadingOlder && !loading) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, loadingOlder, loading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async (reset = false) => {
    if (!otherUserId) {
      console.error('Cannot load messages: otherUserId is missing');
      return;
    }
    
    try {
      if (reset) {
        setLoading(true);
        setMessages([]);
        setNextToken(null);
        setHasMore(false);
      } else {
        setLoadingOlder(true);
      }
      
      const tokenToUse = reset ? null : nextToken;
      const response = await messageAPI.getConversation(otherUserId, 50, null, tokenToUse);
      const newMessages = response.data.messages || [];
      
      if (reset) {
        setMessages(newMessages);
      } else {
        // Prepend older messages to existing messages
        setMessages(prev => [...newMessages, ...prev]);
      }
      
      // Update pagination state
      setNextToken(response.data.nextToken || null);
      setHasMore(response.data.hasMore === true); // Explicitly check for true
      
      // Mark conversation as read (only on initial load)
      if (reset) {
        // Compute conversationId (same logic as backend normalizeConversationId)
        const conversationId = [user.userId, otherUserId].sort().join('#');
        await messageAPI.markConversationRead(otherUserId, conversationId);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      // If user doesn't exist, show error message
      if (error.response?.status === 404 || error.response?.status === 400) {
        alert('Unable to load conversation. The other user may no longer exist.');
        onClose(); // Close the chatbox
      }
    } finally {
      setLoading(false);
      setLoadingOlder(false);
    }
  };

  const handleLoadOlder = async () => {
    if (!loadingOlder && hasMore) {
      // Save current scroll position before loading
      const container = messagesContainerRef.current;
      if (!container) return;
      
      const scrollHeight = container.scrollHeight;
      const scrollTop = container.scrollTop;
      
      // Load older messages
      await loadMessages(false);
      
      // Restore scroll position after DOM updates
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          const heightDifference = newScrollHeight - scrollHeight;
          container.scrollTop = scrollTop + heightDifference;
        }
      });
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const content = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      const response = await messageAPI.sendMessage(
        otherUserId,
        content
      );
      
      // Add new message to list
      setMessages(prev => [...prev, response.data.message]);
      
      // Mark as read immediately (use conversationId from the message)
      const conversationId = response.data.message?.conversationId || 
        [user.userId, otherUserId].sort().join('#');
      await messageAPI.markConversationRead(otherUserId, conversationId);
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
      setNewMessage(content); // Restore message on error
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!otherUserId) return null;

  return (
    <div className="chatbox-container">
      <div className="chatbox-header">
        <div className="chatbox-header-info">
          <h3>{otherUserName}</h3>
        </div>
        <button className="chatbox-close" onClick={onClose} aria-label="Close chat">
          ×
        </button>
      </div>
      
      <div className="chatbox-messages" ref={messagesContainerRef}>
        {loading ? (
          <div className="chatbox-loading">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="chatbox-empty">No messages yet. Start the conversation!</div>
        ) : (
          <>
            {hasMore && (
              <div className="chatbox-load-older">
                <button
                  onClick={handleLoadOlder}
                  disabled={loadingOlder}
                  className="chatbox-load-older-button"
                >
                  {loadingOlder ? 'Loading...' : 'Load Older Messages'}
                </button>
              </div>
            )}
            {messages.map((message) => {
              const isOwn = message.senderId === user.userId;
              return (
                <div key={message.messageId} className={`chatbox-message ${isOwn ? 'own' : 'other'}`}>
                  <div className="chatbox-message-content">
                    {message.content}
                  </div>
                  <div className="chatbox-message-time">
                    {formatTime(message.createdAt)}
                  </div>
                </div>
              );
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <form className="chatbox-input-form" onSubmit={handleSend}>
        <input
          ref={inputRef}
          type="text"
          className="chatbox-input"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          disabled={sending}
        />
        <button 
          type="submit" 
          className="chatbox-send"
          disabled={!newMessage.trim() || sending}
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default ChatBox;

